import { createBrowserRouter, Navigate } from "react-router";
import { Suspense, lazy } from "react";
import { motion } from "framer-motion";

import { AuthLayout } from "./layouts/AuthLayout.jsx";
import { DashboardLayout } from "./layouts/DashboardLayout.jsx";
import { ProtectedRoute } from "./routes/ProtectedRoute.jsx";
import { useReducedMotion } from "framer-motion";

// Lazy load auth components
const Login = lazy(() => import("./features/auth/Login.jsx"));
const Register = lazy(() => import("./features/auth/Register.jsx"));
const ForgotPassword = lazy(() => import("./features/auth/ForgotPassword.jsx"));
const ResetPassword = lazy(() => import("./features/auth/ResetPassword.jsx"));

// Lazy load dashboard components
const ProjectOverview = lazy(() => import("./features/project-overview/ProjectOverview.jsx"));
const EmployeeInvitations = lazy(() => import("./features/invites-and-access/employee-invite/EmployeeInvitations.jsx"));
const EmployeeManagement = lazy(() => import("./features/invites-and-access/employee-management/EmployeeManagement.jsx"));
const History = lazy(() => import("./features/invites-and-access/history/History.jsx"));
const AccessManagement = lazy(() => import("./features/invites-and-access/access-management/AccessManagement.jsx"));
const ProjectTask = lazy(() => import("./features/task-assignments/project-task/ProjectTask.jsx"));
const InternalTask = lazy(() => import("./features/task-assignments/internal-task/InternalTask.jsx"));
const CreateRole = lazy(() => import("./features/invites-and-access/access-management/CreateRole.jsx"));
const UpdateRole = lazy(() => import("./features/invites-and-access/access-management/UpdateRole.jsx"));
const TeamFormation = lazy(() => import("./features/team-management/team-formation/TeamFormation.jsx"));
const SingleProjectTask = lazy(() => import("./features/task-assignments/project-task/SingleProjectTask/SingleProjectTask.jsx"));
const SingleTeamFormation = lazy(() => import("./features/team-management/team-formation/single-team-formation/SingleTeamFormation.jsx"));
const MainLayout = lazy(() => import("./features/master-application-recipe-list/application-recipes/layout/MainLayout.jsx"));
const FinalRecipes = lazy(() => import("./features/master-application-recipe-list/application-recipes/final-recipes/FinalRecipes.jsx"));
const IndevelopmentRecipes = lazy(() => import("./features/master-application-recipe-list/application-recipes/in-development-recipes/IndevelopmentRecipes.jsx"));
const ApplicationCategories = lazy(() => import("./features/master-application-recipe-list/application-categories/ApplicationCategories.jsx"));
const ApplicationSubcategories = lazy(() => import("./features/master-application-recipe-list/application-categories/ApplicationSubcategories.jsx"));
const ApplicationSubSubcategories = lazy(() => import("./features/master-application-recipe-list/application-categories/ApplicationSubSubcategories.jsx"));
const ApplicationTags = lazy(() => import("./features/master-application-recipe-list/application-categories/ApplicationTags.jsx"));
const BFFProductCodeList = lazy(() => import("./features/master-application-recipe-list/bff-product-code-list/BFFProductCodeList.jsx"));
const MasterProjects = lazy(() => import("./features/project-overview/master-project/MasterProjects.jsx"));
const MasterProjectDetails = lazy(() => import("./features/project-overview/master-project/MasterProjectDetails.jsx"));

const ProductDevelopment = lazy(() => import("./features/project-overview/product-development/ProductDevelopment.jsx"));
const ProductDevelopmentDetails = lazy(() => import("./features/project-overview/product-development/ProductDevelopmentDetails.jsx"));

const ApplicationLab = lazy(() => import("./features/project-overview/application-lab/ApplicationLab.jsx"));
const ApplicationLabDetails = lazy(() => import("./features/project-overview/application-lab/ApplicationLabDetails.jsx"));
const Sensory = lazy(() => import("./features/project-overview/sensory/Sensory.jsx"));
const SensoryDetails = lazy(() => import("./features/project-overview/sensory/SensoryDetails.jsx"));
const MasterProjectSchedule = lazy(() => import("./features/project-overview/master-project-schedule/MasterProjectSchedule.jsx"));
const MasterProjectScheduleDetails = lazy(() => import("./features/project-overview/master-project-schedule/MasterProjectScheduleDetails.jsx"));

// eslint-disable-next-line react-refresh/only-export-components
function ErrorPage() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background text-foreground">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold text-red-500">Oops!</h1>
        <p className="mb-4 text-lg">Something went wrong.</p>
        <p className="text-sm text-gray-500">
          Please try refreshing the page or contact support if the problem
          persists.
        </p>
      </div>
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
// eslint-disable-next-line react-refresh/only-export-components
function LoadingPage() {
  return (
    <div className="flex items-center justify-center min-h-screen min-w-screen transition-opacity">
      <div className="text-center">
        <motion.img
          src="/logo.png"
          alt="Loading..."
          className=""
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 2, ease: "easeOut", repeat: Infinity, repeatType: "loop" }}
        />
      </div>
    </div>
  );
}

// Wrapper component for lazy loaded components
function LazyWrapper({ children }) {
  return <Suspense fallback={<LoadingPage />}>{children}</Suspense>;
}

const invitesAndAccessRoutes = [
  { path: "employee-management", element: <LazyWrapper><EmployeeManagement /></LazyWrapper> },
  { path: "employee-invitations", element: <LazyWrapper><EmployeeInvitations /></LazyWrapper> },
  { path: "access-management", element: <LazyWrapper><AccessManagement /></LazyWrapper> },
  { path: "access-management/create-role", element: <LazyWrapper><CreateRole /></LazyWrapper> },
  { path: "access-management/update-role/:roleId", element: <LazyWrapper><UpdateRole /></LazyWrapper> },
  { path: "access-history", element: <LazyWrapper><History /></LazyWrapper> },
  { path: "project-tasks/:taskId", element: <LazyWrapper><SingleProjectTask /></LazyWrapper> },
  { path: "team-formation/:teamId", element: <LazyWrapper><SingleTeamFormation /></LazyWrapper> },
];


const masterApplicationRecipeRoutes = [
  {
    path: "application-recipes",
    element: <LazyWrapper><MainLayout /></LazyWrapper>,
    children: [
      { path: "final-recipes", element: <LazyWrapper><FinalRecipes /></LazyWrapper> },
      { path: "in-development-recipes", element: <LazyWrapper><IndevelopmentRecipes /></LazyWrapper> },
    ],
  },
  {
    path: "application-categories",
    children: [
      { path: "", element: <LazyWrapper><ApplicationCategories /></LazyWrapper> },
      { path: ":categoryId/subcategories", element: <LazyWrapper><ApplicationSubcategories /></LazyWrapper> },
      { path: ":categoryId/subcategories/:subCategoryId/subsubcategories", element: <LazyWrapper><ApplicationSubSubcategories /></LazyWrapper> },
      { path: ":categoryId/subcategories/:subCategoryId/tags", element: <LazyWrapper><ApplicationTags /></LazyWrapper> },
    ]
  },
  {
    path: "bff-product-code-list",
    element: <LazyWrapper><BFFProductCodeList /></LazyWrapper>,
  }
];


const projectOverviewRoutes = [
  {
    path: "project-overview",
    children: [
      { path: "master-projects", element: <LazyWrapper><MasterProjects /></LazyWrapper> },
      { path: "master-projects/:projectId", element: <LazyWrapper><MasterProjectDetails /></LazyWrapper> },
      { path: "product-development", element: <LazyWrapper><ProductDevelopment /></LazyWrapper> },
      { path: "product-development/:projectId", element: <LazyWrapper><ProductDevelopmentDetails /></LazyWrapper> },
      { path: "application-lab", element: <LazyWrapper><ApplicationLab /></LazyWrapper> },
      { path: "application-lab/:projectId", element: <LazyWrapper><ApplicationLabDetails /></LazyWrapper> },
      { path: "sensory", element: <LazyWrapper><Sensory /></LazyWrapper> },
      { path: "sensory/:projectId", element: <LazyWrapper><SensoryDetails /></LazyWrapper> },
      { path: "master-project-schedule", element: <LazyWrapper><MasterProjectSchedule /></LazyWrapper> },
      { path: "master-project-schedule/:projectId", element: <LazyWrapper><MasterProjectScheduleDetails /></LazyWrapper> },
    ]
  }
];

export const router = createBrowserRouter([
  {
    // Root redirect to login
    path: "/",
    element: <Navigate to="/login" replace />,
  },
  {
    // === AUTH LAYOUT ===
    path: "/",
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      { path: "login", element: <LazyWrapper><Login /></LazyWrapper> },
      { path: "register", element: <LazyWrapper><Register /></LazyWrapper> },
      { path: "forgot-password", element: <LazyWrapper><ForgotPassword /></LazyWrapper> },
      { path: "reset-password", element: <LazyWrapper><ResetPassword /></LazyWrapper> },
    ],
  },
  {
    // === DASHBOARD LAYOUT (PROTECTED) ===
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <DashboardLayout />, // UI: sidebar + topbar
        errorElement: <ErrorPage />,
        children: [
          // Dashboard routes
          { path: "dashboard", element: <LazyWrapper><ProjectOverview /></LazyWrapper> },
          { path: "project-overview", element: <LazyWrapper><ProjectOverview /></LazyWrapper> },
          { path: "team-formation", element: <LazyWrapper><TeamFormation /></LazyWrapper> },
          { path: "project-tasks", element: <LazyWrapper><ProjectTask /></LazyWrapper> },
          { path: "internal-tasks", element: <LazyWrapper><InternalTask /></LazyWrapper> },
          ...invitesAndAccessRoutes,
          ...masterApplicationRecipeRoutes,
          ...projectOverviewRoutes,

        ],
      },
    ],
  },
]);
